import { useClient } from "@auth0/auth0-acul-react/signup-id";

import JourneyLayout from "@/components/JourneyLayout";
import ULThemeCard from "@/components/ULThemeCard";
import ULThemeSeparator from "@/components/ULThemeSeparator";
import { isBeneficiaryClient } from "@/utils/beneficiary";
import { detectFlow, journeySteps } from "@/utils/flow";
import { extractTokenValue } from "@/utils/helpers/tokenUtils";
import { applyCareSuperBrand } from "@/utils/theme/careSuperBrand";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

import BeneficiarySignupId from "./beneficiary/BeneficiarySignupId";
import AlternativeLogins from "./components/AlternativeLogins";
import Footer from "./components/Footer";
import Header from "./components/Header";
import SignupIdForm from "./components/SignupIdForm";
import { useSignupIdManager } from "./hooks/useSignupIdManager";

function SignupIdScreen() {
  // Extracting attributes from hook made out of SignupIdInstance class of Auth0 React ACUL SDK
  const { signupId, texts, alternateConnections, locales } =
    useSignupIdManager();

  const showSeparator = alternateConnections && alternateConnections.length > 0;

  const separatorText = texts?.separatorText || locales.page.separator;
  document.title = texts?.pageTitle || locales.page.title;

  applyAuth0Theme(signupId);
  applyCareSuperBrand();

  const socialLoginAlignment = extractTokenValue(
    "--ul-theme-widget-social-buttons-layout"
  );

  const renderSocialLogins = (alignment: "top" | "bottom") => (
    <>
      {alignment === "bottom" && showSeparator && (
        <ULThemeSeparator text={separatorText} />
      )}
      <AlternativeLogins />
      {alignment === "top" && showSeparator && (
        <ULThemeSeparator text={separatorText} />
      )}
    </>
  );

  return (
    // Applying UDS theme overrides using the "theme-universal" class
    // Stepper labels come from the flow: Flow A has 5 steps, Flow B has 4.
    // This screen's own content is identical in both flows — only the
    // right-hand step list differs.
    <JourneyLayout
      currentStep={1}
      steps={journeySteps(detectFlow(signupId.client?.id))}
    >
      <ULThemeCard className="theme-universal w-full gap-0">
        <Header />
        {socialLoginAlignment === "top" && renderSocialLogins("top")}
        <SignupIdForm />
        <Footer />
        {socialLoginAlignment === "bottom" && renderSocialLogins("bottom")}
      </ULThemeCard>
    </JourneyLayout>
  );
}

// signup-id is ACUL tenant-wide, so the Beneficiary Claims client arrives at
// this same screen. The branch happens here, in a wrapper, so neither screen's
// hook order depends on the other's. Members are unaffected:
// isBeneficiaryClient() is false for every client except the one ID baked in
// at build time (src/utils/beneficiary.ts).
function SignupIdEntry() {
  const client = useClient();
  return isBeneficiaryClient(client?.id) ? (
    <BeneficiarySignupId />
  ) : (
    <SignupIdScreen />
  );
}

export default SignupIdEntry;
